// ============================================================================
// O estado das telas da Central do Perito — em memória, lido do banco e gravado nele (000385)
// ============================================================================
// Portado de `src/lib/store.ts` da Central do Perito (03/11). O desenho é o de lá, e ele é bom:
//
//   * CARREGA do banco quando a ferramenta abre (`carregar`) e é LIMPO na saída (`limpar`);
//   * cada ação faz o que sempre fez em memória — a tela responde na hora — e ENFILEIRA a gravação no banco;
//   * a FILA É SERIAL, e isso não é detalhe: criar uma perícia grava a perícia, o histórico e as
//     tarefas-modelo, e as tarefas têm chave estrangeira para a perícia; em paralelo, chegariam fora de ordem;
//   * SE UMA GRAVAÇÃO FALHAR, a tela avisa uma vez e, quando a fila esvaziar, recarrega tudo do banco — o que
//     está na tela passa a ser o que está salvo. Melhor perder a última alteração visivelmente do que mostrar
//     como salvo o que não está.
//
// O que mudou:
//   * sem zustand (o hub não o tem): um store mínimo com `useSyncExternalStore`, mesma forma de uso;
//   * sem o `toast` do sonner: o aviso mora no próprio estado (`aviso`), e a tela o desenha;
//   * `carregar()` não recebe o id do aluno: quem sabe quem é o dono é o banco (`perito_ler`);
//   * o STATUS da perícia é derivado da etapa (`statusDaEtapa`), e não editado — lá a edição o gravava, e o
//     kanban não, então a perícia na etapa 10 podia continuar "em andamento";
//   * o prazo das tarefas-modelo conta do dia de São Paulo (`datas.ts`), e não do dia UTC.
// ============================================================================
import { useSyncExternalStore } from "react";
import { buscarModelo, modelosDaEtapa, type TemplateTarefa } from "./modelos";
import { apagarArquivos, apagarDocumento, carregarDados, enderecoDoDocumento, enviarDocumento, gravar } from "./dados";
import { hoje, maisDias } from "./datas";
import { ETAPA_LABEL, etapaNumDe, statusDaEtapa, type EtapaNum } from "./pipeline";
import type { ChecklistItem, Documento, FiltroSalvo, HistoricoEntry, Honorario, Pericia, Prazo, State, Tarefa } from "./tipos";

function uid(): string {
  return crypto.randomUUID();
}

const FORMATO_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** As colunas de id são `uuid`; o id em outro formato que alguma tela entregue vira um uuid novo. */
function idValido(id: string | undefined): string {
  return id && FORMATO_UUID.test(id) ? id : uid();
}

function agoraIso(): string {
  return new Date().toISOString();
}

/** Monta uma tarefa a partir de um modelo. */
function tarefaDoModelo(modelo: TemplateTarefa, periciaId?: string, prazo?: string): Tarefa {
  return {
    id: uid(),
    pericia_id: periciaId,
    modelo_id: modelo.id,
    titulo: modelo.titulo,
    descricao: modelo.descricao,
    prazo: prazo ?? maisDias(hoje(), modelo.prazoDias),
    // O prazo vindo do despacho não é sugestão; o calculado pelo modelo é.
    prazoSugerido: !prazo,
    prioridade: modelo.prioridade,
    status: "pendente",
    checklist: modelo.checklist.map((texto) => ({ id: uid(), texto, feito: false })),
    criadoEm: agoraIso(),
  };
}

// ============================================================================
// O store mínimo
// ============================================================================
type Definir<S> = (parcial: Partial<S> | ((s: S) => Partial<S>)) => void;

function criarStore<S extends object>(fabrica: (set: Definir<S>, get: () => S) => S) {
  let estado: S;
  const ouvintes = new Set<() => void>();
  const get = () => estado;
  const set: Definir<S> = (parcial) => {
    const p = typeof parcial === "function" ? parcial(estado) : parcial;
    estado = { ...estado, ...p };
    ouvintes.forEach((o) => o());
  };
  estado = fabrica(set, get);
  const assinar = (o: () => void) => {
    ouvintes.add(o);
    return () => ouvintes.delete(o);
  };
  /** O estado inteiro; ele só troca de referência quando alguma ação muda alguma coisa. */
  const usar = () => useSyncExternalStore(assinar, get, get);
  return Object.assign(usar, { getState: get, setState: set });
}

// ============================================================================
// A fila de gravação
// ============================================================================
let fila: Promise<unknown> = Promise.resolve();
let pendentes = 0;
let precisaRecarregar = false;

/** Põe a operação no fim da fila e devolve o resultado dela. */
function enfileirar<T>(operacao: () => Promise<T>): Promise<T> {
  pendentes++;
  const resultado = fila.then(operacao);
  fila = resultado
    .then(() => undefined, () => undefined)
    .finally(() => {
      pendentes--;
      aoEsvaziarAFila();
    });
  return resultado;
}

/** A gravação de uma ação da tela: se falhar, avisa e agenda a recarga. */
function gravarDepois(oQue: string, operacao: () => Promise<unknown>) {
  enfileirar(operacao).catch((erro) => {
    console.error(`[perito] não salvou ${oQue}:`, erro);
    precisaRecarregar = true;
    // Uma falha costuma arrastar as seguintes (a tarefa sem a perícia que falhou): um aviso só.
    usePerito.setState({ aviso: "Não foi possível salvar uma alteração. Seus dados vão ser recarregados do banco." });
  });
}

function aoEsvaziarAFila() {
  if (pendentes > 0 || !precisaRecarregar) return;
  precisaRecarregar = false;
  const { carregado, carregar } = usePerito.getState();
  if (!carregado) return;
  carregar().catch(() => {
    usePerito.setState({ aviso: "Não foi possível recarregar seus dados. Recarregue a página." });
  });
}

/** Há gravação na fila? Quem fecha a aba com gravação pendente perde a alteração, e a tela pergunta. */
export function haGravacaoPendente(): boolean {
  return pendentes > 0;
}

// ============================================================================
// O store
// ============================================================================
interface Acoes {
  carregar: () => Promise<void>;
  limpar: () => void;
  limparAviso: () => void;

  addPericia: (
    p: Omit<Pericia, "id" | "criadoEm" | "atualizadoEm" | "favorito" | "status"> & { favorito?: boolean },
    opcoes?: { prazoAceitacao?: string; semTarefasAutomaticas?: boolean },
  ) => Pericia;
  updatePericia: (id: string, patch: Partial<Pericia>) => void;
  deletePericia: (id: string) => void;
  toggleFavorito: (id: string) => void;
  moverEtapa: (id: string, etapa: EtapaNum) => void;

  addTarefa: (t: Omit<Tarefa, "id" | "criadoEm" | "status" | "checklist"> & { checklist?: ChecklistItem[]; status?: Tarefa["status"] }) => Tarefa;
  updateTarefa: (id: string, patch: Partial<Tarefa>) => void;
  deleteTarefa: (id: string) => void;
  toggleChecklist: (tarefaId: string, itemId: string) => void;
  aplicarModelosDaEtapa: (periciaId: string, etapa: EtapaNum, opcoes?: { prazoAceitacao?: string }) => number;
  criarTarefaDeModelo: (modeloId: string, periciaId?: string, opcoes?: { prazo?: string; prazoSugerido?: boolean }) => Tarefa | null;
  modelosDaPericia: (periciaId: string) => Set<string>;
  addChecklistItem: (tarefaId: string, texto: string) => void;
  updateChecklistItem: (tarefaId: string, itemId: string, patch: Partial<ChecklistItem>) => void;
  deleteChecklistItem: (tarefaId: string, itemId: string) => void;

  addPrazo: (p: Omit<Prazo, "id" | "cumprido"> & { cumprido?: boolean }) => Prazo;
  updatePrazo: (id: string, patch: Partial<Prazo>) => void;
  deletePrazo: (id: string) => void;

  addHonorario: (periciaId: string, h: Omit<Honorario, "id">) => void;
  updateHonorario: (periciaId: string, id: string, patch: Partial<Honorario>) => void;
  deleteHonorario: (periciaId: string, id: string) => void;

  addHistorico: (h: Omit<HistoricoEntry, "id" | "ts">) => void;

  addFiltroSalvo: (nome: string, filtros: FiltroSalvo["filtros"]) => FiltroSalvo;
  deleteFiltroSalvo: (id: string) => void;

  anexarDocumento: (periciaId: string, arquivo: Blob, nome: string, tipo: string) => Promise<Documento>;
  removerDocumento: (id: string) => void;
  enderecoDoDocumento: (doc: Documento, baixar?: boolean) => Promise<string>;

  exportarJson: () => string;
}

type Extra = { carregado: boolean; erro: string | null; podeEscrever: boolean; aviso: string | null };
export type PeritoStore = State & Acoes & Extra;

const vazio: State & Extra = {
  pericias: [], tarefas: [], prazos: [], historico: [], filtrosSalvos: [], documentos: [],
  carregado: false, erro: null, podeEscrever: false, aviso: null,
};

export const usePerito = criarStore<PeritoStore>((set, get) => {
  const periciaPorId = (id: string | undefined) => (id ? get().pericias.find((p) => p.id === id) : undefined);

  /** Grava a perícia como ela está AGORA em memória. */
  const gravarPericia = (id: string | undefined) => {
    const p = periciaPorId(id);
    if (p) gravarDepois("a perícia", () => gravar.pericia(p));
  };

  return {
    ...vazio,

    carregar: async () => {
      const r = await carregarDados();
      if ("erro" in r) {
        set({ erro: r.erro, carregado: true });
        return;
      }
      set({ ...r.estado, podeEscrever: r.podeEscrever, erro: null, carregado: true });
    },
    limpar: () => set({ ...vazio }),
    limparAviso: () => set({ aviso: null }),

    addPericia: (p, opcoes) => {
      const agora = agoraIso();
      const etapa = etapaNumDe(p);
      const pericia: Pericia = {
        ...p,
        status: statusDaEtapa(etapa),
        honorarios: (p.honorarios ?? []).map((h) => ({ ...h, id: idValido(h.id) })),
        favorito: p.favorito ?? false,
        id: uid(),
        criadoEm: agora,
        atualizadoEm: agora,
      };
      set((s) => ({ pericias: [pericia, ...s.pericias] }));
      gravarDepois("a perícia", () => gravar.pericia(pericia));
      for (const h of pericia.honorarios) gravarDepois("o honorário", () => gravar.honorario(pericia.id, h));
      get().addHistorico({ acao: "Perícia criada", pericia_id: pericia.id, detalhe: pericia.numeroProcesso });
      // O pacote inicial: as tarefas da etapa em que a perícia começa (o cadastro manual escolhe as tarefas
      // na própria tela e desliga isto).
      if (!opcoes?.semTarefasAutomaticas) get().aplicarModelosDaEtapa(pericia.id, etapa, opcoes);
      return pericia;
    },
    updatePericia: (id, patch) => {
      // Os honorários têm ações e tabela próprias: aqui eles não seriam gravados, então nem entram. E o status
      // não se edita: ele acompanha a etapa.
      const { honorarios: _h, status: _s, ...campos } = patch;
      void _h;
      void _s;
      set((s) => ({
        pericias: s.pericias.map((p) => {
          if (p.id !== id) return p;
          const novo = { ...p, ...campos, atualizadoEm: agoraIso() };
          return { ...novo, status: statusDaEtapa(etapaNumDe(novo)) };
        }),
      }));
      gravarPericia(id);
    },
    deletePericia: (id) => {
      const caminhos = get().documentos.filter((d) => d.pericia_id === id).map((d) => d.caminho);
      set((s) => ({
        pericias: s.pericias.filter((p) => p.id !== id),
        documentos: s.documentos.filter((d) => d.pericia_id !== id),
        // As tarefas e os prazos ficam, soltos, como lá — o banco faz o mesmo (ON DELETE SET NULL).
        tarefas: s.tarefas.map((t) => (t.pericia_id === id ? { ...t, pericia_id: undefined } : t)),
        prazos: s.prazos.map((p) => (p.pericia_id === id ? { ...p, pericia_id: undefined } : p)),
      }));
      // Os arquivos saem ANTES da perícia: o CASCADE apaga as linhas dos documentos, mas não alcança o
      // Storage. Se a remoção falhar, a exclusão não acontece — melhor que arquivo órfão com dado de processo.
      gravarDepois("a exclusão da perícia", async () => {
        await apagarArquivos(caminhos);
        await gravar.apagar("perito_pericias", id);
      });
      get().addHistorico({ acao: "Perícia excluída", pericia_id: id });
    },
    toggleFavorito: (id) => {
      set((s) => ({ pericias: s.pericias.map((p) => (p.id === id ? { ...p, favorito: !p.favorito } : p)) }));
      gravarPericia(id);
    },
    moverEtapa: (id, etapa) => {
      const atual = periciaPorId(id);
      if (!atual || etapaNumDe(atual) === etapa) return;
      set((s) => ({
        pericias: s.pericias.map((p) =>
          p.id === id ? { ...p, etapa_atual: etapa, status: statusDaEtapa(etapa), atualizadoEm: agoraIso() } : p,
        ),
      }));
      gravarPericia(id);
      get().addHistorico({ acao: `Etapa alterada para ${etapa}. ${ETAPA_LABEL[etapa]}`, pericia_id: id, detalhe: atual.numeroProcesso });
      // As tarefas automáticas da nova etapa.
      get().aplicarModelosDaEtapa(id, etapa);
    },

    addTarefa: (t) => {
      const tarefa: Tarefa = { ...t, status: t.status ?? "pendente", checklist: t.checklist ?? [], id: uid(), criadoEm: agoraIso() };
      if (tarefa.status === "concluida") tarefa.concluidoEm = agoraIso();
      set((s) => ({ tarefas: [tarefa, ...s.tarefas] }));
      gravarDepois("a tarefa", () => gravar.tarefa(tarefa));
      get().addHistorico({ acao: `Tarefa criada: ${tarefa.titulo}`, pericia_id: tarefa.pericia_id });
      return tarefa;
    },
    updateTarefa: (id, patch) => {
      let atualizada: Tarefa | undefined;
      set((s) => ({
        tarefas: s.tarefas.map((t) => {
          if (t.id !== id) return t;
          const nova = { ...t, ...patch };
          // O prazo alterado pelo aluno deixa de ser sugestão.
          if (!("prazoSugerido" in patch) && patch.prazo !== undefined && patch.prazo !== t.prazo) nova.prazoSugerido = false;
          // A data da conclusão é a da primeira vez, e sai quando a tarefa volta (lá ficava, e mentia).
          if (nova.status === "concluida" && !t.concluidoEm) nova.concluidoEm = agoraIso();
          if (nova.status !== "concluida") nova.concluidoEm = undefined;
          atualizada = nova;
          return nova;
        }),
      }));
      const t = atualizada;
      if (t) gravarDepois("a tarefa", () => gravar.tarefa(t));
    },
    modelosDaPericia: (periciaId) =>
      new Set(get().tarefas.filter((t) => t.pericia_id === periciaId && t.modelo_id).map((t) => t.modelo_id as string)),
    aplicarModelosDaEtapa: (periciaId, etapa, opcoes) => {
      const existentes = get().modelosDaPericia(periciaId);
      const removidos = new Set(periciaPorId(periciaId)?.modelosRemovidos ?? []);
      const novas = modelosDaEtapa(etapa)
        .filter((m) => !existentes.has(m.id) && !removidos.has(m.id))
        .map((m) => tarefaDoModelo(m, periciaId, m.usaPrazoAceitacao && opcoes?.prazoAceitacao ? opcoes.prazoAceitacao : undefined));
      if (novas.length === 0) return 0;
      set((s) => ({ tarefas: [...novas, ...s.tarefas] }));
      for (const t of novas) gravarDepois("as tarefas da etapa", () => gravar.tarefa(t));
      get().addHistorico({ acao: `Tarefas da etapa ${etapa}. ${ETAPA_LABEL[etapa]} criadas (${novas.length})`, pericia_id: periciaId });
      return novas.length;
    },
    criarTarefaDeModelo: (modeloId, periciaId, opcoes) => {
      const modelo = buscarModelo(modeloId);
      if (!modelo) return null;
      const tarefa = tarefaDoModelo(modelo, periciaId, opcoes?.prazo);
      if (opcoes?.prazoSugerido !== undefined) tarefa.prazoSugerido = opcoes.prazoSugerido;
      const voltaDosRemovidos = Boolean(periciaPorId(periciaId)?.modelosRemovidos?.includes(modeloId));
      set((s) => ({
        tarefas: [tarefa, ...s.tarefas],
        // Adicionada de novo pelo aluno: sai da lista das removidas.
        pericias: s.pericias.map((p) =>
          p.id === periciaId && p.modelosRemovidos?.includes(modeloId)
            ? { ...p, modelosRemovidos: p.modelosRemovidos.filter((m) => m !== modeloId) }
            : p,
        ),
      }));
      gravarDepois("a tarefa", () => gravar.tarefa(tarefa));
      if (voltaDosRemovidos) gravarPericia(periciaId);
      get().addHistorico({ acao: `Tarefa criada: ${tarefa.titulo}`, pericia_id: periciaId });
      return tarefa;
    },
    deleteTarefa: (id) => {
      const tarefa = get().tarefas.find((t) => t.id === id);
      const marcaRemovido = Boolean(tarefa?.modelo_id && tarefa.pericia_id);
      set((s) => ({
        tarefas: s.tarefas.filter((t) => t.id !== id),
        // A tarefa-modelo removida não volta sozinha quando a etapa mudar.
        pericias: marcaRemovido
          ? s.pericias.map((p) =>
              p.id === tarefa?.pericia_id
                ? { ...p, modelosRemovidos: [...new Set([...(p.modelosRemovidos ?? []), tarefa?.modelo_id as string])] }
                : p,
            )
          : s.pericias,
      }));
      gravarDepois("a exclusão da tarefa", () => gravar.apagar("perito_tarefas", id));
      if (marcaRemovido) gravarPericia(tarefa?.pericia_id);
    },
    addChecklistItem: (tarefaId, texto) => {
      const t = get().tarefas.find((x) => x.id === tarefaId);
      if (t) get().updateTarefa(tarefaId, { checklist: [...t.checklist, { id: uid(), texto, feito: false }] });
    },
    updateChecklistItem: (tarefaId, itemId, patch) => {
      const t = get().tarefas.find((x) => x.id === tarefaId);
      if (t) get().updateTarefa(tarefaId, { checklist: t.checklist.map((c) => (c.id === itemId ? { ...c, ...patch } : c)) });
    },
    deleteChecklistItem: (tarefaId, itemId) => {
      const t = get().tarefas.find((x) => x.id === tarefaId);
      if (t) get().updateTarefa(tarefaId, { checklist: t.checklist.filter((c) => c.id !== itemId) });
    },
    toggleChecklist: (tarefaId, itemId) => {
      const t = get().tarefas.find((x) => x.id === tarefaId);
      if (t) get().updateTarefa(tarefaId, { checklist: t.checklist.map((c) => (c.id === itemId ? { ...c, feito: !c.feito } : c)) });
    },

    addPrazo: (p) => {
      const prazo: Prazo = { ...p, id: uid(), cumprido: p.cumprido ?? false };
      set((s) => ({ prazos: [prazo, ...s.prazos] }));
      gravarDepois("o prazo", () => gravar.prazo(prazo));
      return prazo;
    },
    updatePrazo: (id, patch) => {
      let atualizado: Prazo | undefined;
      set((s) => ({
        prazos: s.prazos.map((p) => {
          if (p.id !== id) return p;
          atualizado = { ...p, ...patch };
          return atualizado;
        }),
      }));
      const p = atualizado;
      if (p) gravarDepois("o prazo", () => gravar.prazo(p));
    },
    deletePrazo: (id) => {
      set((s) => ({ prazos: s.prazos.filter((p) => p.id !== id) }));
      gravarDepois("a exclusão do prazo", () => gravar.apagar("perito_prazos", id));
    },

    addHonorario: (periciaId, h) => {
      const honor: Honorario = { ...h, id: uid() };
      set((s) => ({
        pericias: s.pericias.map((p) => (p.id === periciaId ? { ...p, honorarios: [...p.honorarios, honor], atualizadoEm: agoraIso() } : p)),
      }));
      gravarDepois("o honorário", () => gravar.honorario(periciaId, honor));
      get().addHistorico({ acao: `Honorário adicionado: ${h.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`, pericia_id: periciaId });
    },
    updateHonorario: (periciaId, id, patch) => {
      let atualizado: Honorario | undefined;
      set((s) => ({
        pericias: s.pericias.map((p) =>
          p.id !== periciaId
            ? p
            : {
                ...p,
                honorarios: p.honorarios.map((h) => {
                  if (h.id !== id) return h;
                  atualizado = { ...h, ...patch };
                  return atualizado;
                }),
                atualizadoEm: agoraIso(),
              },
        ),
      }));
      const h = atualizado;
      if (h) gravarDepois("o honorário", () => gravar.honorario(periciaId, h));
    },
    deleteHonorario: (periciaId, id) => {
      set((s) => ({ pericias: s.pericias.map((p) => (p.id !== periciaId ? p : { ...p, honorarios: p.honorarios.filter((h) => h.id !== id) })) }));
      gravarDepois("a exclusão do honorário", () => gravar.apagar("perito_honorarios", id));
    },

    addHistorico: (h) => {
      const linha: HistoricoEntry = { ...h, id: uid(), ts: agoraIso() };
      set((s) => ({ historico: [linha, ...s.historico].slice(0, 500) }));
      gravarDepois("o histórico", () => gravar.historico(linha));
    },

    addFiltroSalvo: (nome, filtros) => {
      const f: FiltroSalvo = { id: uid(), nome, filtros, criadoEm: agoraIso() };
      set((s) => ({ filtrosSalvos: [f, ...s.filtrosSalvos] }));
      gravarDepois("o filtro", () => gravar.filtro(f));
      return f;
    },
    deleteFiltroSalvo: (id) => {
      set((s) => ({ filtrosSalvos: s.filtrosSalvos.filter((f) => f.id !== id) }));
      gravarDepois("a exclusão do filtro", () => gravar.apagar("perito_filtros", id));
    },

    anexarDocumento: async (periciaId, arquivo, nome, tipo) => {
      // Pela fila: a perícia pode ter acabado de ser criada, e a linha do documento tem chave estrangeira
      // para ela. A tela espera o resultado, então a falha volta para quem chamou.
      const doc = await enfileirar(() => enviarDocumento(periciaId, arquivo, nome, tipo));
      set((s) => ({ documentos: [doc, ...s.documentos] }));
      get().addHistorico({ acao: `Documento anexado: ${nome}`, pericia_id: periciaId });
      return doc;
    },
    removerDocumento: (id) => {
      const doc = get().documentos.find((d) => d.id === id);
      if (!doc) return;
      set((s) => ({ documentos: s.documentos.filter((d) => d.id !== id) }));
      gravarDepois("a exclusão do documento", () => apagarDocumento(doc));
      get().addHistorico({ acao: `Documento removido: ${doc.nome}`, pericia_id: doc.pericia_id });
    },
    enderecoDoDocumento: (doc, baixar = false) => enderecoDoDocumento(doc, baixar),

    exportarJson: () => {
      const s = get();
      return JSON.stringify(
        {
          exportadoEm: agoraIso(),
          pericias: s.pericias,
          tarefas: s.tarefas,
          prazos: s.prazos,
          historico: s.historico,
          filtrosSalvos: s.filtrosSalvos,
          // Só a lista: os arquivos ficam no bucket e saem pela aba Documentos.
          documentos: s.documentos.map(({ caminho: _c, ...d }) => {
            void _c;
            return d;
          }),
        },
        null,
        2,
      );
    },
  };
});
